import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { filters } from "./utils/filters";
import DefaultColumnFilter from "./Filters/DefaultColumnFilter";
import { useBlockLayout, useColumnOrder, useExpanded, useFilters, useGlobalFilter, usePagination, useResizeColumns, useRowSelect, useSortBy, useTable,} from "react-table";
import useOptionFilter from "./hooks/useOptionFilter";
import { IconButton, Pagination, Stack, TableCell, TableContainer, TableHead, TablePagination, TableRow, Chip, Box, Tooltip} from "@mui/material";
import MaUTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import { addTooltipRow } from "./utils/addTooltipRow";
import SimpleTable from "./SimpleTable";
import styled from "@emotion/styled";
import ButtonGroupCustom from "./buttons/ButtonGroupCustom";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import TagDialog from "../../../scenes/dashboard/components/genomics-dashboard/components/results/components/toolbar/tag_dialog/TagDialog";
import { variantFinder } from "../../../scenes/dashboard/components/genomics-dashboard/components/results/components/variant-table/gpap-table/variant_finder";
import { renderToStringAlt } from "../../../scenes/instand/reports/create-report/shared-components/renderToString.js";
import BootstrapTooltip from "../../../gpap-lib/components/BootstrapTooltip";
import { CNVPreviousTags } from "./buttons/CNVPreviousTags";
import IGVLinks from "../../../scenes/dashboard/components/genomics-dashboard/components/results/components/variant-table/gpap-table/components/IGVLinks";
import Typography from "@mui/material/Typography";
import "../../../scenes/dashboard/components/genomics-dashboard/components/variant-tabs/components/variant-info/variant-info.css";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TableRowNoData from "./TableRowNoData.js";
import Label_Menu from "../../../scenes/dashboard/components/genomics-dashboard/components/results/components/toolbar/label-menu/Label_Menu.js";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {ANNOTATE_VARIANTS } from "../../../scenes/dashboard/reducers/study_actions.js";
import { getAnalysisAnnotations } from "../../../scenes/dashboard/components/genomics-dashboard/components/results/components/variant-table/getAnnotations.js";
import { findLabelForVariant } from "../../../scenes/dashboard/components/genomics-dashboard/components/results/components/variant-table/variantIdentity.js";
import { useTranslation } from "react-i18next";
import MappedText from "../../../scenes/instand/reports/create-report/shared-components/mapText.js";

const hasTableValue = (value) => value !== undefined && value !== null && value !== "";

function TableComponentForVariants({
  columns,
  data,
  type,
  nestedColumns,
  buttons,
  states,
  tooltipCompact,
  renderFeatures,
  chipFull,
  total,
  isSNVSelected
}) {
  const { t, i18n } = useTranslation();

  const [expandedRows, setExpandedRows] = useState({});
  const [copied, setCopied] = useState(false);

  const DATA_TYPE = "CNV";
  const filterTypes = useMemo(() => filters(), []);

  // get from REDUX

  // TODO:return selected_row;

  const selected_table_row = useSelector(state => state.selected_table_rows.find(s => s.data_type === DATA_TYPE));

  const dataRef = useRef(data); // Ref to keep track of previous data
  const dispatch = useDispatch();
  const studySettings = useSelector(state => state.studySettings);


  const handleArrowNavigation = useCallback((event) => {
    if (!firstPageRows || firstPageRows.length === 0) return;
  
    const currentIndex = firstPageRows.findIndex(
      (row) => row.id === states.rowIdSelected
    );
  
    let newIndex = currentIndex;
  
    if (event.key === 'ArrowDown') {
      newIndex = Math.min(currentIndex + 1, firstPageRows.length - 1);
    } else if (event.key === 'ArrowUp') {
      newIndex = Math.max(currentIndex - 1, 0);
    } else {
      return;
    }
  
    const newRow = firstPageRows[newIndex];
  
    if (newRow && newRow.id !== states.rowIdSelected) {
      states.setRowIdSelected(newRow.id);
      states.setSelected(newRow.original);
  
      getAdditionalInfo(newRow).then((res) => {
        if (res) {
          newRow.toggleRowExpanded(false);
          states.setIdExpanded({ id: newRow.id, expanded: false, selected: true });
        }
      });
      
  
      requestAnimationFrame(() => {
        const rowElement = document.querySelector(`.row${newRow.id}`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  
    event.preventDefault();
  }, [firstPageRows, states.rowIdSelected]);
  
  useEffect(() => {
    window.addEventListener('keydown', handleArrowNavigation);
    return () => window.removeEventListener('keydown', handleArrowNavigation);
  }, [handleArrowNavigation]); 

  const handleCopy = () => {
    if (selected_variant?.length > 0) {
      const textToCopy = `${selected_variant[0].SV_chrom}-${selected_variant[0].SV_start}-${selected_variant[0].SV_end}`;
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); 
    }
  };

  const DEFAULT_LABELS = ['Candidate', 'Follow-Up', 'Uncertain', 'Not relevant'];

  const labelledStr = useSelector(
    s => JSON.stringify(
           getAnalysisAnnotations(s.studySettings, s.studyBucket).labelled
         )
  );

  const labelled = useMemo(() => {
    const parsed = JSON.parse(labelledStr);
    DEFAULT_LABELS.forEach(l => { if (!parsed[l]) parsed[l] = []; });
    return parsed;
  }, [labelledStr]);


  const buildLabelReadyVariant = (v) => ({
    ...v,
   uniqueName: `${v.SV_chrom}-${v.SV_start}-${v.SV_end}-${v.SV_type}-${v.Experiment_ID}`
  });

  const handleLabelVariants = (annotation_type, labelType) => {
    if (annotation_type !== "LABEL") return;
    if (!selected_variant?.length) return;

    dispatch({
      type: ANNOTATE_VARIANTS,
      annotation_type: "LABEL",
      label: labelType,
      variants: selected_variant.map(buildLabelReadyVariant),
      study_id: studySettings.id,
      analysis_id: studySettings.current_analysis,
      query_id: studySettings.current_query,
    });
  };

  // the selected_variant object;
  const selected_variant = (selected_table_row !== undefined) ? selected_table_row.data_row : [];
  const samples = selected_variant[0]?.samples_somatic || [selected_variant[0]?.Experiment_ID] || [];


  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  //Sorting helpers
  const sortNum = useCallback((rowA, rowB, columnId) => {
    const [numA, numB] = [rowA, rowB].map((r) => parseFloat(r.values[columnId]));
    return !isNaN(numA) && !isNaN(numB)
      ? numA - numB
      : ("" + rowA.values[columnId]).localeCompare("" + rowB.values[columnId], undefined, {
          numeric: true
        });
  }, []);

  const chromosomeSort = useCallback((rowA, rowB, columnId) => {
    const order = (c) => (c === "X" ? 24 : c === "Y" ? 25 : parseInt(c, 10));
    return order(rowA.values[columnId]) - order(rowB.values[columnId]);
  }, []);

  const geneSort = useCallback((rowA, rowB, columnId) => {
    const firstGene = (g) => (g ? String(g).split(/,|\s/).filter(Boolean)[0].toUpperCase() : "");
    return firstGene(rowA.values[columnId]).localeCompare(firstGene(rowB.values[columnId]));
  }, []);

  const formatNumber = useCallback((n, d) => {
    if (n === null || n === undefined) return "";
    const s = n.toString();
    const idx = s.indexOf(".");
    return idx !== -1 && s.length - idx - 1 > d ? Number(n).toFixed(d) : s;
  }, []);

  const numColumns = useMemo(() => {
    return columns.map(group => {
      const newColumns = group.columns.map(column => {
        if (column.accessor === "Internal_Freq") {
          return {
            ...column,
            sortType: sortNum,
            Cell: ({ value }) => {
              if (!hasTableValue(value)) {
                return <>N/A</>;
              }

              return (
                <BootstrapTooltip title={String(value)}>
                  <span style={{
                    textDecoration: 'underline',
                    textDecorationStyle: 'dotted',
                    textDecorationColor: '#BDBDBD',
                    textUnderlineOffset: '3px'
                  }}>{formatNumber(value, 6)}</span>
                </BootstrapTooltip>
              );
            }
          };
        } else if (column.accessor === "Gene_name") {
          return {
            ...column,
            sortType: geneSort,
          };
        } else if (column.accessor === "SV_chrom") {
          return {
            ...column,
            sortType: chromosomeSort,
          };
        } else {
          const isNumeric = data.every(item =>
            item[column.accessor] !== undefined &&
            !isNaN(parseFloat(item[column.accessor])) &&
            isFinite(item[column.accessor])
          );
          if (isNumeric) {
            return {
              ...column,
              sortType: sortNum,
              Cell: ({ cell }) => {
                const { content, truncated } = addTooltipRow(cell, tooltipCompact, data);
                return (
                  <BootstrapTooltip title={truncated ? content : ""}>
                    <span>{cell.value}</span>
                  </BootstrapTooltip>
                );
              }
            };
          } else {
            return column;
          }
        }
      });

      return {
        ...group,
        columns: newColumns,
      };
    });
  }, [columns, sortNum, chromosomeSort, geneSort, data]);

  // Effect to update the table data only if the data changes
  useEffect(() => {
    if (data !== dataRef.current) {
      // Update table data
      dataRef.current = data; // Update ref to new data
    }
  }, [data]);

  const getAdditionalInfo = async (row) => {
    return !row.isExpanded;

  };


  const isFlagged = (row) => {
    let dataRow = row.original ? row.original : row;

    if (!dataRow || !dataRow.SV_chrom) {
      return false;
    }

    let annotations = undefined;
    let tagged_variants = states.all_tagged_variants;

    let variant_found_cnv = variantFinder(dataRow, annotations, tagged_variants, "CNV");
    if (variant_found_cnv !== undefined && variant_found_cnv.length !== 0) {

      // In search_across mode, filter by sample to only show tagged icon for the specific sample
      // if (studySettings?.analysisType === "search_across" && dataRow.Experiment_ID) {
      //  const filteredBySample = variant_found_cnv.filter(p => p.sample === dataRow.Experiment_ID);
      //  return filteredBySample.length > 0;
      //}

      return true;
    }

    let variant_found_somatic = variantFinder(dataRow, annotations, tagged_variants, "SNV_Somatic");
    if (variant_found_somatic !== undefined && variant_found_somatic.length !== 0) {
      return true;
    }

    return false;
  };


  const getNumberOfTags = (row) => {

    let dataRow = row.original ? row.original : row;

    if (!dataRow || !dataRow.SV_chrom) {
      return false;
    }

    let annotations = undefined;
    let tagged_variants = states.all_tagged_variants;

    let variant_found_cnv = variantFinder(dataRow, annotations, tagged_variants, "CNV");
    
    // In search_across mode, filter by sample to only show tagged icon for the specific sample
    //if (studySettings?.analysisType === "search_across" && dataRow.Experiment_ID) {
    //  const filteredBySample = variant_found_cnv.filter(p => p.sample === dataRow.Experiment_ID);
    //  return filteredBySample.length;
    //}

    return variant_found_cnv.length;
 
 
  }


  const isCausative = (row) => {
    let dataRow = row.original ? row.original : row;

    if (!dataRow || !dataRow.SV_chrom) {
      return false;
    }
    let annotations = undefined;
    let tagged_variants = states.all_tagged_variants;

    let matched_variant = variantFinder(dataRow, annotations, tagged_variants, "CNV");

    let anyCausative = matched_variant?.some((variant) => {
      return variant?.status === "Causative";
    });

    return anyCausative;


  };

  const getLabelsForRow = useCallback(
    (row) =>{
      const label = findLabelForVariant(row, labelled);
      return label ? [label] : [];
    },
    [labelled]
  );

  const buildUniqueName = v =>`${v.SV_chrom}-${v.SV_start}-${v.SV_end}-${v.SV_type}-${v.Experiment_ID}`;

  const dataWithIsFlagged = useMemo(() => {
    return data.map(row => {
      const uniqueName = buildUniqueName(row);
      const flagged = isFlagged(row); 
      return{
      ...row,
      uniqueName,
      isFlagged: flagged,
      isCausative: isCausative(row),
      numberOfTags: getNumberOfTags(row),
      labels: getLabelsForRow({ ...row, uniqueName }).slice(-1),
    };
  });
  }, [data, states.all_tagged_variants, labelled]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    visibleColumns,
    page,
    pageCount,
    gotoPage,
    setPageSize,
    allColumns,
    getToggleHideAllColumnsProps,
    state: { pageIndex, pageSize, selectedRowIds, sortBy, filters: activeFilters },
    resetResizing,
    rows,
  } = useTable(
    {
      columns: numColumns,
      data: dataWithIsFlagged,
      defaultColumn,
      filterTypes,
      autoResetPage: false,
      autoResetFilters: false,
      autoResetSortBy: false,
      initialState: {
        sortBy: [
          { id: 'SV_chrom', desc: false },
          { id: 'SV_start', desc: false },
          { id: 'SV_end', desc: false },
        ],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useBlockLayout,
    useResizeColumns,
    useOptionFilter(columns, type, states, setExpandedRows, dataWithIsFlagged)
  );

  useEffect(() => {
    gotoPage(0);
  }, [activeFilters, gotoPage]);

  useEffect(() => {

    // at the moment of creating a new query, "Run Query", take predfined gene list and get data from server

    // filter with gene list updated

    if (isSNVSelected) {
      // reset selected CNV
      states.setIdExpanded({ id: null, expanded: false, selected: false });
      states.setRowIdSelected(null);
      // empty redux store for cnv
      // reset CNV row only

      states.setSelected(null);

    }


  }, [isSNVSelected])


  useEffect(() => {
    if (pageIndex === pageCount - 1) {
      //window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pageIndex, pageCount]);



  const firstPageRows = page;


  return (
    <>
      <Stack direction="column" spacing={2}>
        {selected_variant?.length > 0 ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              width: "23%",
              padding: "10px",
              borderRadius: "6px",
              "&:hover": { backgroundColor: "#ebebeb" },
            }}
          >
            <Typography sx={{ fontWeight: "bold" }} className="ref">
            <MappedText text="Chr"/>: {selected_variant[0].SV_chrom}
            </Typography>
            <Typography sx={{ fontWeight: "bold" }} className="ref">
                <MappedText text="Start"/>: {selected_variant[0].SV_start}
            </Typography>
            <Typography sx={{ fontWeight: "bold" }} className="ref">
                <MappedText text="End"/>: {selected_variant[0].SV_end}
            </Typography>

            <Tooltip title={copied ? t("Copied!") : t("Copy to clipboard")}>
              <IconButton onClick={handleCopy} size="small">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>

        ) : (
          <Typography>{t("No variant selected")}</Typography>
        )}

        {selected_variant.length > 0 && (
          <Stack direction="row" spacing={2} alignItems="center">
            <TagDialog
              annotate_variants={(label, array, type) => console.log(type)}
              tagged_variants={states.all_tagged_variants}
              getAllTaggedVariants={states.getAllTaggedVariants}
              dataType={DATA_TYPE}
            />
            <Label_Menu
              annotate_variants={handleLabelVariants}
            />
            <CNVPreviousTags tagged_variants={states.all_tagged_variants} />
            <IGVLinks samples={samples} dataType={DATA_TYPE} />
            <ButtonGroupCustom
              allColumns={allColumns}
              getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
              resetResizing={resetResizing}
              buttons={buttons}
              states={states}
              renderFeatures={renderFeatures}
            />
          </Stack>
        )}
      </Stack>
      <br />

      <TableContainer sx={{ maxHeight: '600px', overflowY: 'auto' }}>
        <MaUTable {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup, headerGroupIndex) => (
              <React.Fragment key={headerGroup.id}>
                <TableRow
                  sx={{
                    backgroundColor: headerGroupIndex === 0 ? '#3E69A9' : '#EAEAEA'
                  }}
                  {...headerGroup.getHeaderGroupProps()}
                >
                  {headerGroup.headers.map((column, columnIndex) => (
                    <TableCell {...column.getHeaderProps(column.getSortByToggleProps())}
                      sx={{
                        textAlign: "center",
                        borderRight: '1px solid #C4C4C4', '&:last-child': { borderRight: '0' }, position: 'sticky', top: 0, zIndex: 1, padding: "2px"
                      }}>
                      <div style={{ color: headerGroupIndex === 0 ? 'white' : 'inherit' }}>
                        {column.tooltip ? (
                          <BootstrapTooltip title={column.tooltip} forceUnderline={true}>
                            {column.render("Header")}
                          </BootstrapTooltip>

                        ) : (
                          column.render("Header")
                        )}
                        {column.id !== 'expansion' && (
                          <span>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <IconButton>
                                  <ArrowUpward />
                                </IconButton>
                              ) : (
                                <IconButton>
                                  <ArrowDownward />
                                </IconButton>
                              )
                            ) : (
                              ""
                            )}
                          </span>
                        )}

                      </div>

                    </TableCell>
                  ))}
                </TableRow>
                {headerGroup.headers.some((column) => column.canFilter) ? (
                  <TableRow {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <TableCell {...column.getHeaderProps()}
                        sx={{
                          display: 'flex!important',
                          textAlign: "center",
                          padding: "8px!important",
                          alignItems: 'center!important',
                          justifyContent: 'left!important',
                          position: 'sticky', top: 0, zIndex: 1,
                        }}    >
                        {column.canFilter && column.filterType && column.filterType.type !== 'none' ? column.render('Filter') : null}
                      </TableCell>
                    ))}
                  </TableRow>
                ) : null}
              </React.Fragment>
            ))}
            <TableRow style={{ display: "none" }}>
              <TableCell
                colSpan={visibleColumns.length}
                style={{ textAlign: "left" }}
              ></TableCell>
            </TableRow>
          </TableHead>

          <TableBodyCustom
            rowId={states.idExpanded.id}
            {...getTableBodyProps()}
          >

            {(firstPageRows.length !== 0 && total !== -1)
              ? firstPageRows.map((row, i) => {

                prepareRow(row);
                return (
                  <>
                    <TableRowCustom
                      className={`row${row.id}`}
                      {...row.getRowProps()}
                      rowId={states.idExpanded.id}
                      states={states}
                      expanded={states}
                      onClick={() => {
                        // Check if the clicked row is already selected
                        if (
                          states.idExpanded.id === row.id &&
                          states.idExpanded.selected
                        ) {
                          // Deselect the row
                          row.toggleRowExpanded(false);
                          states.setIdExpanded({
                            id: null,
                            expanded: false,
                            selected: false,
                          });
                          states.setRowIdSelected(null);
                          // empty redux store for cnv
                          states.setSelected(null);
                        } else {
                          // Close any currently expanded row
                          if (states.idExpanded.id !== null) {
                            const currentExpandedRow = firstPageRows.find(
                              (r) => r.id === states.idExpanded.id
                            );
                            if (currentExpandedRow) {
                              currentExpandedRow.toggleRowExpanded(false);
                            }
                          }

                          // Expand the clicked row
                          getAdditionalInfo(row).then((res) => {
                            if (res) {
                              row.toggleRowExpanded(true);
                              states.setIdExpanded({
                                id: row.id,
                                expanded: true,
                                selected: true,
                              });
                              states.setRowIdSelected(row.id);
                              states.setSelected(row.original);
                            }
                          });
                        }
                      }}
                      onMouseEnter={() => {
                        document.querySelector(
                          `.row${row.id}`
                        ).style.backgroundColor = "#EAEAEA";
                        document.querySelector(`.row${row.id}`).style.cursor =
                          "pointer";
                      }}
                      onMouseLeave={() => {
                        document.querySelector(
                          `.row${row.id}`
                        ).style.backgroundColor = "#fff";
                      }}
                    >
                      {row.cells.map((cell) => {
                        const { truncated, content } = addTooltipRow(
                          cell,
                          tooltipCompact,
                          data
                        );

                        return (
                          <TableCell {...cell.getCellProps()}>
                            <BootstrapTooltip
                              title={truncated ? `${cell.value}` : ""}
                              isTruncated={truncated}
                            >
                              <div
                                style={
                                  truncated
                                    ? {
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100px",
                                      }
                                    : {}
                                }
                              >
                                {(() => {
                                  const matchedData = chipFull
                                    ?.flatMap((item) => item.cells)
                                    .find((data) => data.data === cell.value);
                                  if (matchedData) {
                                    return (
                                      <Chip
                                        size="small"
                                        label={content}
                                        sx={{
                                          backgroundColor: matchedData.color,
                                          color: matchedData.textColor,
                                        }}
                                      />
                                    );
                                  }
                                  if (
                                    typeof cell.column.Header === "function"
                                  ) {
                                    return content;
                                  }

                                  return (
                                    <div>{hasTableValue(cell.value) ? content : <>N/A</>}</div>
                                  );
                                })()}
                              </div>
                            </BootstrapTooltip>
                          </TableCell>
                        );
                      })}
                    </TableRowCustom>

                    {row.isExpanded && expandedRows[row.id]?.expanded ? (
                      <TableRow>
                        <TableCell
                          sx={{ backgroundColor: "#F9F9F9" }}
                          colSpan={visibleColumns.length}
                        >
                          {expandedRows[row.id]?.data ? (
                            <SimpleTable
                              columns={nestedColumns}
                              data={expandedRows[row.id].data}
                              states={states}
                            />
                          ) : (
                            <div>Loading...</div>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </>
                );
              })
              : <TableRowNoData
                message={renderToStringAlt("No variants have passed the selected filters")}
                submessage={renderToStringAlt("To display variants, try using less restrictive filters or expanding the gene list")}
                colSpan={5}
              />
            }
          </TableBodyCustom>
        </MaUTable>
      </TableContainer>

      <br />

      <div style={{ display: "flex", alignItems: "center", height: "50px", justifyContent: "space-between" }}>

        <TablePagination
          labelRowsPerPage={t("Rows per page:")}
          component="div"
          rowsPerPageOptions={[10, 25]}
          count={rows.length}
          rowsPerPage={pageSize}
          page={pageIndex}
          onPageChange={(event, newPage) => gotoPage(newPage)}
          onRowsPerPageChange={(event) => setPageSize(Number(event.target.value))}
          style={{ '& .MuiTablePagination-actions': { display: 'none' } }}
          labelDisplayedRows={({ from, to, count }) =>
            t("table_pagination_range", { page: pageIndex + 1, from, to, count })
          }

        />

        <Pagination
          count={pageCount}
          page={pageIndex + 1}
          onChange={(event, value) => gotoPage(value - 1)}
          showFirstButton
          showLastButton
        />

      </div>
    </>
  );
}


export default TableComponentForVariants;

const TableRowCustom = styled(TableRow)`
height: 50px;
  & > td {
    display: flex!important;
    align-items: center;
    div {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      //height: 40px;
      vertical-align: middle;
    }
  }
`;

// Styled component for the no data message
const NoDataMessageCell = styled(TableCell)`
  /*display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;*/
  color: grey;
  font-size: 16px;
  /*
  padding: 20px;
  */
  width: 100%;
  font-weight: bold;
`;

const NoDataSubMessageCell = styled(TableCell)`
  /*display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;*/
  color: grey;
  font-size: 16px;
  /*
  padding: 20px;
  */
  width: 100%;
`;




const TableBodyCustom = styled(TableBody)`
  ${(props) => {
    return (
      props.rowId &&
      `.row${props.rowId} > td {
    background-color: #C6DBEF !important;
  }`
    );
  }}
`;